import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { TaskInterface } from '@vendasta/task';
import { firstValueFrom, forkJoin, Observable, of, ReplaySubject } from 'rxjs';
import { map, mergeMap, shareReplay, take, tap } from 'rxjs/operators';
import { ProjectListCardService } from './project-list-card.service';
import { PROJECTS_ICON_GREEN, TaskStatus } from '../shared/constants';
import { AccountGroup } from '@galaxy/account-group';
import { BadgeColor } from '@vendasta/galaxy/badge/src/badge.component';
import { SendMessageDialogComponent } from '../send-message-dialog/send-message-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'project-tracker-list-card',
  templateUrl: './project-list-card.component.html',
  styleUrls: ['./project-list-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ProjectListCardComponent implements OnInit {
  @Input() vendorTasks = false;
  @Input() accountGroupId: string;
  @Input() partnerId: string;
  @Input() pageSize = 4;

  projects$: Observable<TaskInterface[]>;
  loadingProjects$: Observable<boolean> = this.projectListCardService.loadingProjects$;
  isEmptyState$$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
  isEmptyState$ = this.isEmptyState$$.asObservable();
  showViewAll$: Observable<boolean>;
  accountGroupInfo$$: ReplaySubject<AccountGroup> = new ReplaySubject<AccountGroup>(1);
  accountGroupInfo$ = this.accountGroupInfo$$.asObservable();

  projectsIconGreen = PROJECTS_ICON_GREEN;
  ProjectStatusOrder: Map<TaskStatus, number> = new Map([
    [TaskStatus.OPEN, 0],
    [TaskStatus.IN_PROGRESS, 1],
    [TaskStatus.WAITING_ON_CUSTOMER, 2],
    [TaskStatus.COMPLETED, 3],
  ]);

  status = TaskStatus;

  constructor(
    private projectListCardService: ProjectListCardService,
    private dialog: MatDialog,
    private readonly translateService: TranslateService,
  ) {}

  ngOnInit(): void {
    const projectsList$ = this.projectListCardService.getAllProjects(
      this.partnerId,
      this.accountGroupId,
      this.pageSize,
      this.vendorTasks,
      [TaskStatus.IN_PROGRESS, TaskStatus.OPEN, TaskStatus.WAITING_ON_CUSTOMER],
    );

    this.projects$ = projectsList$.pipe(
      mergeMap((projects) => {
        if (projects.length >= this.pageSize) {
          return of(projects);
        }

        return forkJoin([
          of(projects),
          this.projectListCardService.getAllProjects(
            this.partnerId,
            this.accountGroupId,
            this.pageSize - projects.length,
            this.vendorTasks,
            [TaskStatus.COMPLETED],
          ),
        ]).pipe(map(([inProgressProjects, completedProjects]) => inProgressProjects.concat(completedProjects)));
      }),
      map((projects: TaskInterface[]) =>
        projects.sort((a, b) => (this.ProjectStatusOrder[a.status] < this.ProjectStatusOrder[b.status] ? -1 : 1)),
      ),
      shareReplay(1),
    );

    this.projectListCardService
      .getAccountGroupInfo(this.accountGroupId)
      .pipe(
        take(1),
        tap((agInfo) =>
          agInfo ? this.accountGroupInfo$$.next(agInfo) : this.accountGroupInfo$$.next({} as AccountGroup),
        ),
      )
      .subscribe();
    this.showViewAll$ = this.projects$.pipe(
      take(1),
      map((projects) => projects?.length > 0),
    );
    this.projects$
      .pipe(
        take(1),
        tap((projects) => (projects?.length === 0 ? this.isEmptyState$$.next(true) : this.isEmptyState$$.next(false))),
      )
      .subscribe();
  }

  goToProjectDetail(project: TaskInterface): void {
    this.projectListCardService.goToProjectDetail(project);
  }

  goToAccountProjectList(): void {
    this.accountGroupInfo$
      .pipe(take(1))
      .subscribe((agInfo) =>
        this.projectListCardService.goToAccountProjectList(agInfo?.accountGroupId, agInfo?.napData?.companyName),
      );
  }

  getStatusBadgeColor(status: string): BadgeColor {
    switch (status) {
      case TaskStatus.IN_PROGRESS:
      case TaskStatus.OPEN: {
        return 'blue';
      }
      case TaskStatus.WAITING_ON_CUSTOMER: {
        return 'yellow';
      }
      case TaskStatus.COMPLETED: {
        return 'green';
      }
    }
  }

  async sendMessage(project: TaskInterface): Promise<void> {
    const accountGroupInfo = await firstValueFrom(this.accountGroupInfo$);
    this.dialog
      .open(SendMessageDialogComponent, {
        width: '500px',
        data: {
          partnerId: this.partnerId,
          accountGroupInfo: accountGroupInfo,
          project: project,
        },
        autoFocus: false,
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe((recipientOrganization) => {
        if (recipientOrganization?.id) {
          const message = this.translateService.instant('PROJECT_LIST_CARD.INBOX_PREFILLED_MESSAGE', {
            companyName: accountGroupInfo.napData.companyName,
            taskId: project.identity?.taskId,
            title: project.title,
            taskType: project.taskType,
            accountGroupId: accountGroupInfo.accountGroupId,
          });

          this.projectListCardService.redirectToConversation(
            this.partnerId,
            recipientOrganization.id,
            recipientOrganization.type,
            message,
          );
        }
      });
  }
}
